import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Leaf, Sparkles, Check } from "lucide-react";
import Seo from "@/components/Seo";

const CITIES = {
  mumbai: {
    name: "Mumbai",
    region: "IN-MH",
    hero: "https://images.pexels.com/photos/8477058/pexels-photo-8477058.jpeg",
    blurb: "Mumbai's finest homes",
    localities: "South Mumbai, Bandra, Juhu, Worli, Powai and the western & central suburbs",
    keywords: "chef hire Mumbai, private chef Mumbai, hire a chef in Mumbai, home chef Mumbai, personal chef Mumbai, private chef for home Mumbai, cook for HNI homes Mumbai",
  },
  bengaluru: {
    name: "Bengaluru",
    region: "IN-KA",
    hero: "https://images.unsplash.com/photo-1590741664176-7fbd7e2592a0",
    blurb: "Bengaluru's leafy enclaves and gated communities",
    localities: "Indiranagar, Koramangala, Whitefield, Jayanagar, Sadashivanagar and Electronic City",
    keywords: "chef hire Bengaluru, private chef Bengaluru, hire a chef in Bangalore, home chef Bengaluru, personal chef Bangalore, private chef for home Bengaluru, cook for HNI homes Bangalore",
  },
  indore: {
    name: "Indore",
    region: "IN-MP",
    hero: "https://images.unsplash.com/photo-1559561724-732dbca7be1e",
    blurb: "Indore's growing community of discerning households",
    localities: "Vijay Nagar, Old Palasia, New Palasia, South Tukoganj and Rau",
    keywords: "chef hire Indore, private chef Indore, hire a chef in Indore, home chef Indore, personal chef Indore, private chef for home Indore, cook for HNI homes Indore",
  },
  "delhi-ncr": {
    name: "Delhi NCR",
    region: "IN-DL",
    hero: "https://images.unsplash.com/photo-1414235077428-338989a2e8c0",
    blurb: "Delhi NCR's most prestigious addresses",
    localities: "Lutyens' Delhi, Chanakyapuri, Golf Links, Vasant Vihar, Gurugram (DLF) and Noida",
    keywords: "chef hire Delhi, private chef Delhi NCR, hire a chef in Delhi, home chef Gurugram, personal chef Noida, private chef for home Delhi, cook for HNI homes Delhi NCR",
  },
  hyderabad: {
    name: "Hyderabad",
    region: "IN-TG",
    hero: "https://images.pexels.com/photos/27138849/pexels-photo-27138849.jpeg",
    blurb: "Hyderabad's finest residences",
    localities: "Banjara Hills, Jubilee Hills, Gachibowli, Kondapur and HITEC City",
    keywords: "chef hire Hyderabad, private chef Hyderabad, hire a chef in Hyderabad, home chef Hyderabad, personal chef Hyderabad, private chef for home Hyderabad, cook for HNI homes Hyderabad",
  },
};

export default function CityChefs({ city }) {
  const c = CITIES[city] || CITIES.mumbai;
  const { name, localities } = c;

  const faqs = [
    {
      q: `How much does it cost to hire a private chef in ${name}?`,
      a: `Private chef hire in ${name} starts from ₹75,000 per month for our India roster, up to ₹1.25L; international chefs range ₹90,000–₹3.55L. Chef Hire charges a one-time placement fee equal to one month's salary plus 18% GST — no percentage commission. The staff member stays on Chef Hire's payroll and reports to us, and the monthly salary is billed through Chef Hire.`,
    },
    {
      q: `What kind of chefs can I hire in ${name}?`,
      a: `A curated, background-verified roster — Modern Indian, Coastal, Continental, Japanese and Italian — focused on healthy cooking, curated household menus and innovative food offerings for HNI homes.`,
    },
    {
      q: `Do you offer one-time catering as well as full-time chefs?`,
      a: `Yes. Alongside full-time private chef postings we offer curated Mini Catering menus and a monthly Chef's Table, plus festive experiences like our Diwali gourmet sit-down from ₹899 per person.`,
    },
    {
      q: `Which areas of ${name} do you serve?`,
      a: `All of ${name} including ${localities} — plus Mumbai, Bengaluru and Indore, and other cities on request.`,
    },
  ];

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  return (
    <div data-testid={`city-page-${city}`}>
      <Seo
        title={`Private Chef Hire in ${name} — From ₹75,000/month | Chef Hire`}
        description={`Hire a private chef in ${name} for your home. Background-verified Indian & international chefs from ₹75,000/month, focused on healthy cooking, curated meals & innovative food. One-time fee of one month's salary + GST, staff on our payroll.`}
        path={`/private-chef-${city}`}
        keywords={c.keywords}
        jsonLd={jsonLd}
      />

      <section className="relative overflow-hidden grain">
        <div className="absolute inset-0">
          <img src={c.hero} alt={`Private chef cooking in a ${name} home`} className="w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A]/65 via-[#0A0A0A]/75 to-[#0A0A0A]" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 pt-20 pb-28 md:pt-28 md:pb-32">
          <span className="chip text-[#F5B041]">{name} · HNI homes</span>
          <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-[1.02] max-w-4xl tracking-tight mt-6">
            Private chef hire in {name},<br />
            <span className="font-script text-[#F5B041] text-5xl sm:text-6xl lg:text-7xl">from ₹75,000 / month.</span>
          </h1>
          <p className="mt-8 text-lg text-neutral-300 max-w-2xl leading-relaxed">
            Chef Hire places background-verified private chefs in {c.blurb} — focused on healthy cooking,
            curated household menus and innovative food offerings. One-time fee of one month's salary + GST — staff on our payroll, reporting to us.
          </p>
          <div className="mt-10 flex flex-wrap gap-3">
            <Link to="/chefs" className="btn-primary small-caps text-[11px] inline-flex items-center gap-2" data-testid={`city-browse-${city}`}>
              Meet {name} chefs <ArrowRight size={14} />
            </Link>
            <Link to="/diwali" className="btn-ghost small-caps text-[11px] inline-flex items-center gap-2">
              Diwali gourmet sit-down
            </Link>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { icon: Leaf, t: "Healthy cooking", d: "Nutrition-led menus — balanced, seasonal and tailored to your household's goals." },
            { icon: Sparkles, t: "Curated meals", d: "Household menus planned and rotated by chefs who cook to your family's taste." },
            { icon: ShieldCheck, t: "Innovative food", d: "Modern techniques and inventive plating — restaurant finesse, at home, every day." },
          ].map((f) => (
            <div key={f.t} className="card-lux p-8">
              <f.icon size={22} className="text-[#F5B041]" />
              <div className="font-serif-menu text-2xl mt-4">{f.t}</div>
              <p className="text-neutral-400 mt-2 leading-relaxed">{f.d}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 pb-20 grid md:grid-cols-2 gap-14">
        <div>
          <div className="small-caps text-neutral-500 mb-4">Why Chef Hire in {name}</div>
          <h2 className="font-serif-display text-4xl leading-tight">The city's discreet private-chef desk.</h2>
          <ul className="mt-8 space-y-4">
            {[
              "From ₹75,000/month — one-time fee of one month's salary + GST, staff on our payroll",
              `Background-verified, NDA-bound chefs across ${name}`,
              "Indian, Coastal, Continental, Japanese & Italian specialists",
              "Full-time postings, Mini Catering and the monthly Chef's Table",
              "A single concierge point of contact, start to plate",
            ].map((t) => (
              <li key={t} className="flex items-start gap-3 text-neutral-300">
                <Check size={16} className="text-[#F5B041] mt-1 shrink-0" /> {t}
              </li>
            ))}
          </ul>
        </div>
        <div>
          <div className="small-caps text-neutral-500 mb-4">FAQ</div>
          <div className="space-y-5">
            {faqs.map((f) => (
              <div key={f.q} className="border-b border-white/10 pb-5">
                <div className="font-serif-menu text-xl">{f.q}</div>
                <p className="text-neutral-400 mt-2 leading-relaxed text-sm">{f.a}</p>
              </div>
            ))}
          </div>
          <Link to="/chefs" className="btn-primary small-caps text-[11px] inline-flex items-center gap-2 mt-8" data-testid={`city-cta2-${city}`}>
            Browse the {name} roster <ArrowRight size={14} />
          </Link>
        </div>
      </section>
    </div>
  );
}
